/**
 * Initialize evaluation chapters from a catalog when switching catalogs.
 * Ported from GSA openacr-editor src/utils/updateEvaluation.js.
 * Preserves existing chapter data where criteria match; adds new chapters/criteria; does not remove criteria that no longer exist in the new catalog (simplified vs GSA which removes WCAG 2.1-only criteria when switching to 2.0).
 * @param {object} catalog - Editor catalog shape { chapters: Array<{ id, criteria }> }
 * @param {object} currentEvaluation - Current evaluation state
 * @returns {object} New evaluation with chapters initialized for the catalog
 */
export default function initializeChaptersForCatalog(catalog, currentEvaluation) {
  const chapters = catalog?.chapters ?? [];
  const nextChapters = { ...(currentEvaluation?.chapters ?? {}) };
  const webChapterNote = "See WCAG 2.x Section";

  chapters.forEach((chapter) => {
    const chapterId = chapter.id;
    const existing = nextChapters[chapterId] ?? { notes: "", disabled: false, criteria: [] };

    if (!nextChapters[chapterId]) {
      nextChapters[chapterId] = { notes: "", disabled: false, criteria: [] };
    }

    const criteria = chapter.criteria ?? [];
    const existingCriteria = nextChapters[chapterId].criteria ?? [];
    const nextCriteria = [];

    criteria.forEach((catalogCrit) => {
      const critId = catalogCrit.id;
      const components = catalogCrit.components ?? [];
      const existingCrit = existingCriteria.find((c) => String(c.num) === String(critId));

      if (existingCrit) {
        const nextComps = [...(existingCrit.components ?? [])];
        components.forEach((compName) => {
          const name = typeof compName === "string" ? compName : compName?.name;
          if (!name) return;
          const hasComp = nextComps.some((c) => c.name === name);
          if (!hasComp) {
            nextComps.push({ name, adherence: { level: "", notes: "" } });
          }
        });
        nextCriteria.push({ ...existingCrit, components: nextComps });
      } else {
        nextCriteria.push({
          num: critId,
          components: components.map((compName) => {
            const name = typeof compName === "string" ? compName : compName?.name;
            return { name: name ?? "", adherence: { level: "", notes: "" } };
          }),
        });
      }
    });

    const nextChapterState = {
      ...nextChapters[chapterId],
      notes: existing.notes ?? "",
      disabled: Boolean(existing.disabled),
      criteria: nextCriteria,
    };

    if (chapterId === "web") {
      nextChapterState.notes = webChapterNote;
      nextChapterState.disabled = true;
    }

    nextChapters[chapterId] = nextChapterState;
  });

  return { ...currentEvaluation, chapters: nextChapters };
}
